import { KbNodesRequestMessageSchema, Message, CollectionRegistry } from '../types';
import { logger } from '../utils/logger';

/**
 * Handle unified KB Nodes (Knowledge Base) management request
 * Supports operations: create, update, delete, getAll, getOne, search, getByCategory, getByUser, getCategories, getTags
 *
 * Strategy: Uses database collections for all operations
 */
export async function handleKbNodesRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  // Helper function to execute collection operations
  const executeCollection = async (collection: string, op: string, params: any): Promise<any> => {
    const handler = collections[collection]?.[op];
    if (!handler) {
      throw new Error(`Collection '${collection}' or operation '${op}' not found`);
    }
    return await handler(params);
  };

  try {
    const request = KbNodesRequestMessageSchema.parse(data);
    const { id, payload, from } = request;
    const { operation, data: requestData } = payload;

    // Extract fields from request data
    const nodeId = requestData?.id;
    const title = requestData?.title;
    const content = requestData?.content;
    const category = requestData?.category;
    const tags = requestData?.tags;
    const type = requestData?.type;
    const createdBy = requestData?.createdBy;
    const updatedBy = requestData?.updatedBy;
    const userId = requestData?.userId;
    const query = requestData?.query;
    const filters = requestData?.filters;
    const limit = requestData?.limit;
    const offset = requestData?.offset;

    // Route to appropriate operation handler
    switch (operation) {
      case 'create':
        await handleCreate(id, { title, content, category, tags, type, createdBy }, executeCollection, sendMessage, from.id);
        break;

      case 'update':
        await handleUpdate(id, nodeId, { title, content, category, tags, type, updatedBy }, executeCollection, sendMessage, from.id);
        break;

      case 'delete':
        await handleDelete(id, nodeId, executeCollection, sendMessage, from.id);
        break;

      case 'getAll':
        await handleGetAll(id, limit, offset, executeCollection, sendMessage, from.id);
        break;

      case 'getOne':
        await handleGetOne(id, nodeId, executeCollection, sendMessage, from.id);
        break;

      case 'search':
        await handleSearch(id, { query, category, tags, type, createdBy, limit, offset }, executeCollection, sendMessage, from.id);
        break;

      case 'getByCategory':
        await handleGetByCategory(id, category, limit, offset, executeCollection, sendMessage, from.id);
        break;

      case 'getByUser':
        await handleGetByUser(id, userId, limit, offset, executeCollection, sendMessage, from.id);
        break;

      case 'getCategories':
        await handleGetCategories(id, executeCollection, sendMessage, from.id);
        break;

      case 'getTags':
        await handleGetTags(id, executeCollection, sendMessage, from.id);
        break;

      default:
        sendResponse(id, {
          success: false,
          error: `Unknown operation: ${operation}`
        }, sendMessage, from.id);
    }

  } catch (error) {
    logger.error('Failed to handle KB nodes request:', error);
    sendResponse(null, {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    }, sendMessage);
  }
}

/**
 * Handle create KB node operation
 */
async function handleCreate(
  id: string,
  nodeData: {
    title?: string;
    content?: string;
    category?: string;
    tags?: string[];
    type?: 'global' | 'user' | 'query';
    createdBy?: string;
  },
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  const { title, content, category, tags, type, createdBy } = nodeData;

  // Validate required fields
  if (!title || title.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Title is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  if (!content || content.trim().length === 0) {
    sendResponse(id, {
      success: false,
      error: 'Content is required and cannot be empty'
    }, sendMessage, clientId);
    return;
  }

  if (!createdBy) {
    sendResponse(id, {
      success: false,
      error: 'createdBy (user ID) is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('kbNodes', 'create', {
      title,
      content,
      category: category || undefined,
      tags: tags || undefined,
      type: type || 'query',
      createdBy
    });

    if (result && result.success) {
      logger.info(`[DB] KB node created successfully: ${title}`);
      sendResponse(id, {
        success: true,
        data: {
          ...result.data,
          message: `Knowledge node '${title}' created successfully`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to create knowledge node'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to create KB node:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to create knowledge node'
    }, sendMessage, clientId);
  }
}

/**
 * Handle update KB node operation
 */
async function handleUpdate(
  id: string,
  nodeId: number | undefined,
  nodeData: {
    title?: string;
    content?: string;
    category?: string;
    tags?: string[];
    type?: 'global' | 'user' | 'query';
    updatedBy?: string;
  },
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  const { title, content, category, tags, type, updatedBy } = nodeData;

  if (!nodeId) {
    sendResponse(id, {
      success: false,
      error: 'Knowledge node ID is required'
    }, sendMessage, clientId);
    return;
  }

  if (!updatedBy) {
    sendResponse(id, {
      success: false,
      error: 'updatedBy (user ID) is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('kbNodes', 'update', {
      id: nodeId,
      title,
      content,
      category,
      tags,
      type,
      updatedBy
    });

    if (result && result.success) {
      logger.info(`[DB] KB node updated successfully, ID: ${nodeId}`);
      sendResponse(id, {
        success: true,
        data: {
          ...result.data,
          message: `Knowledge node updated successfully`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to update knowledge node'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to update KB node:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update knowledge node'
    }, sendMessage, clientId);
  }
}

/**
 * Handle delete KB node operation
 */
async function handleDelete(
  id: string,
  nodeId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  if (!nodeId) {
    sendResponse(id, {
      success: false,
      error: 'Knowledge node ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('kbNodes', 'delete', { id: nodeId });

    if (result && result.success) {
      logger.info(`[DB] KB node deleted successfully, ID: ${nodeId}`);
      sendResponse(id, {
        success: true,
        data: {
          id: nodeId,
          ...result.data,
          message: `Knowledge node deleted successfully`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to delete knowledge node'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to delete KB node:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete knowledge node'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all KB nodes operation
 */
async function handleGetAll(
  id: string,
  limit: number | undefined,
  offset: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('kbNodes', 'getAll', {
      limit: limit || 100,
      offset: offset || 0
    });

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} KB nodes`);
      sendResponse(id, {
        success: true,
        data: {
          nodes: result.data,
          count: result.count,
          message: `Retrieved ${result.count} knowledge nodes`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to retrieve knowledge nodes'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to get all KB nodes:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve knowledge nodes'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get one KB node operation
 */
async function handleGetOne(
  id: string,
  nodeId: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  if (!nodeId) {
    sendResponse(id, {
      success: false,
      error: 'Knowledge node ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('kbNodes', 'getOne', { id: nodeId });

    if (result && result.success) {
      logger.info(`[DB] Retrieved KB node ID: ${nodeId}`);
      sendResponse(id, {
        success: true,
        data: {
          node: result.data,
          message: `Retrieved knowledge node`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to retrieve knowledge node'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to get KB node:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve knowledge node'
    }, sendMessage, clientId);
  }
}

/**
 * Handle search KB nodes operation
 */
async function handleSearch(
  id: string,
  searchParams: {
    query?: string;
    category?: string;
    tags?: string[];
    type?: 'global' | 'user' | 'query';
    createdBy?: string;
    limit?: number;
    offset?: number;
  },
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  const { query, category, tags, type, createdBy, limit, offset } = searchParams;

  try {
    const result = await executeCollection('kbNodes', 'search', {
      query,
      category,
      tags,
      type,
      createdBy,
      limit: limit || 50,
      offset: offset || 0
    });

    if (result && result.success) {
      logger.info(`[DB] Search returned ${result.count} KB nodes`);
      sendResponse(id, {
        success: true,
        data: {
          nodes: result.data,
          count: result.count,
          message: `Search returned ${result.count} knowledge nodes`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to search knowledge nodes'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to search KB nodes:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to search knowledge nodes'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get KB nodes by category operation
 */
async function handleGetByCategory(
  id: string,
  category: string | undefined,
  limit: number | undefined,
  offset: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  if (!category) {
    sendResponse(id, {
      success: false,
      error: 'Category is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('kbNodes', 'getByCategory', {
      category,
      limit: limit || 50,
      offset: offset || 0
    });

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} KB nodes for category: ${category}`);
      sendResponse(id, {
        success: true,
        data: {
          nodes: result.data,
          count: result.count,
          category,
          message: `Retrieved ${result.count} knowledge nodes for category '${category}'`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to retrieve knowledge nodes by category'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to get KB nodes by category:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve knowledge nodes by category'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get KB nodes by user operation
 */
async function handleGetByUser(
  id: string,
  userId: string | undefined,
  limit: number | undefined,
  offset: number | undefined,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  if (!userId) {
    sendResponse(id, {
      success: false,
      error: 'User ID is required'
    }, sendMessage, clientId);
    return;
  }

  try {
    const result = await executeCollection('kbNodes', 'getByUser', {
      userId,
      limit: limit || 50,
      offset: offset || 0
    });

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} KB nodes for user: ${userId}`);
      sendResponse(id, {
        success: true,
        data: {
          nodes: result.data,
          count: result.count,
          userId,
          message: `Retrieved ${result.count} knowledge nodes for user ${userId}`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to retrieve knowledge nodes by user'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to get KB nodes by user:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve knowledge nodes by user'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all categories operation
 */
async function handleGetCategories(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('kbNodes', 'getCategories', {});

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} categories`);
      sendResponse(id, {
        success: true,
        data: {
          categories: result.data,
          count: result.count,
          message: `Retrieved ${result.count} categories`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to retrieve categories'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to get categories:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve categories'
    }, sendMessage, clientId);
  }
}

/**
 * Handle get all tags operation
 */
async function handleGetTags(
  id: string,
  executeCollection: (collection: string, op: string, params: any) => Promise<any>,
  sendMessage: (message: Message) => void,
  clientId?: string
): Promise<void> {
  try {
    const result = await executeCollection('kbNodes', 'getTags', {});

    if (result && result.success) {
      logger.info(`[DB] Retrieved ${result.count} tags`);
      sendResponse(id, {
        success: true,
        data: {
          tags: result.data,
          count: result.count,
          message: `Retrieved ${result.count} tags`
        }
      }, sendMessage, clientId);
      return;
    }

    sendResponse(id, {
      success: false,
      error: 'Failed to retrieve tags'
    }, sendMessage, clientId);
  } catch (error) {
    logger.error('[DB] Failed to get tags:', error);
    sendResponse(id, {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to retrieve tags'
    }, sendMessage, clientId);
  }
}

/**
 * Send KB nodes response
 */
function sendResponse(
  id: string | null,
  res: { success: boolean; error?: string; data?: any },
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id: id || 'unknown',
    type: 'KB_NODES_RES',
    from: { type: 'data-agent' },
    to: {
      type: 'admin',
      id: clientId
    },
    payload: {
      ...res,
    }
  };

  sendMessage(response);
}
